// Libraries
import React, {createRef} from 'react'
import {Link, navigate} from 'gatsby'

// Components
import DuoSection from './duoSection'
import InputFactory from './inputFactory'
import Button from './button'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faArrowLeft, faInfoCircle} from '@fortawesome/pro-regular-svg-icons'
import ReactTooltip from 'react-tooltip'

// Services
import AuthService from '../services/auth'
import EmploymentStatusService from '../services/employmentStatus'
import OrganisationService from '../services/organisation'
import TrustService from '../services/trust'

// Animations
import { Player } from '@lottiefiles/react-lottie-player'
import buttonRegisterJson from '../animations/buttonRegister.json'
import buttonRegisterBgJson from '../animations/buttonRegisterBg.json'

// Initialise new instance of employment status service
const employmentStatusService = new EmploymentStatusService()

// Initialise new instance of auth service
const authService = new AuthService()

// Initialise new instance of organisation service
const orgService = new OrganisationService()

// Initialise new instance of trust service
const trustService = new TrustService()

class RegisterForm extends React.Component {
	constructor(props) {
		super(props)

		// Get active process from props
		const { process } = props

		// Set step, first step 7 number of steps dependent on active process
		let step = 1,
			firstStep = 1,
			numSteps = 4

		if(process === 'non-nhs-carer') {
			step = 0
			firstStep = 0
			numSteps = 4
		}
		else if(process === 'organisation') {
			step = 5
			firstStep = 5
			numSteps = 1
		}

		// Initial state
		this.state = {
			step: step,
			firstStep: firstStep,
			numSteps: numSteps,
			isLoading: false,
			formData: {
				address: {
					is_primary: 1,
					is_billing: 1,
					is_shipping: 1,
					country_code: 'GB'
				}
			},
			errors: {},
			employmentStatuses: [],
			organisations: [],
			registerButtonLottie: null,
			registerButtonLottieState: null,
			registerBgButtonLottie: null,
			registerBgButtonLottieState: null
		}
	}

	async componentDidMount() {
		// Get active registration process from props
		const { process } = this.props

		// Check if current process is NHS or non-NHS carer
		if(process === 'nhs-carer' || process === 'non-nhs-carer') {
			// Get employment statuses
			let response = await employmentStatusService.get(),
				data = response?.data

			let employmentStatuses = []

			// Populate array with employment statuses
			data.map(function (employmentStatus) {
				employmentStatuses.push({
					label: employmentStatus?.name,
					value: employmentStatus?.id,
				})

				return employmentStatuses
			})

			// Set employment statuses state
			this.setState({employmentStatuses: employmentStatuses})

			// Get trusts
			response = await trustService.get()
			data = response?.data

			// Set dynamic variable for organisations
			let trusts = []

			// Populate array with trusts
			data.map(function (trust) {
				trusts.push({
					label: trust?.name,
					value: trust?.id,
				})

				return trust
			})

			// Set trusts state
			this.setState({trusts: trusts})
		}

		// Check if current process is organisation
		if(process === 'non-nhs-carer') {
			// Get organisations
			let response = await orgService.get(),
				data = response?.data

			// Set dynamic variable for organisations
			let organisations = []

			// Populate array with employment statuses
			data.map(function (organisation) {
				organisations.push({
					label: organisation?.name,
					value: organisation?.id,
				})

				return organisations
			})

			// Set organisations state
			this.setState({organisations: organisations})
		}
	}

	renderStep0() {
		// Get all input values & errors
		const { errors, organisations } = this.state

		const noOptionsMessage = () => "Please enter the first five characters of your organisation to search"

		return (
			<>
				<InputFactory
					type="select"
					name="id"
					label="Organisation*"
					error={errors?.id}
					onChange={this.handleInputChange}
					options={organisations}
					className="select-field"
					requireSearch={true}
					requireSearchMinLength="5"
					noOptionsMessage={noOptionsMessage}
					onKeyPress={this.handleKeyPress}
				/>

				<Link to="/register/organisation/not-found" className="link--white link--underline">I can't find my organisation</Link>
			</>
		)
	}

	renderStep1() {
		// Get all input values & errors
		const { formData, errors } = this.state

		// Get whether NHS from props
		const { process } = this.props

		// Set label dependent on whether NHS
		const label = process === 'nhs-carer' 
			? 'Enter your NHS email address*' 
			: <>Enter your work email address*<br/>
				<small>If you do not have a work email address, you can use your personal email</small>
			  </>

		return (
			<InputFactory
				type="email"
				name="email"
				label={label}
				placeholder="Email"
				value={formData?.email}
				error={errors?.email}
				onChange={this.handleInputChange}
				onKeyPress={this.handleKeyPress}
			/>
		)
	}

	renderStep2() {
		// Get all input values & errors
		const { formData, errors } = this.state

		// Get whether NHS from props
		const { process } = this.props

		// Set label dependent on whether NHS
		const label = process === 'nhs-carer' ? 'Upload your NHS work ID*' : 'Please upload proof of your employee number / payroll number / ID*'

		return (
			<>
				<InputFactory
					type="file"
					name="image"
					label={label}
					value={formData?.image}
					error={errors?.image}
					onChange={this.handleInputChange}
					onKeyPress={this.handleKeyPress}
				/>

				{process === 'nhs-carer' && <button onClick={() => this.setState({step: 3})} className="link--white link--underline">
					I don't have a work ID</button>}
			</>
		)
	}

	renderStep3() {
		// Get all input values & errors
		const { errors, trusts } = this.state

		const noOptionsMessage = () => "Please enter the first five characters of your trust to search"

		return (
			<>
				<InputFactory
					type="select"
					name="id"
					label="Select your Trust*"
					error={errors?.id}
					onChange={this.handleInputChange}
					options={trusts}
					className="select-field"
					requireSearch={true}
					requireSearchMinLength="5"
					noOptionsMessage={noOptionsMessage}
					onKeyPress={this.handleKeyPress}
				/>

				<button onClick={() => this.setState({step: 2})} className="link--white link--underline">
					Back to work ID upload</button>
			</>
		)
	}

	renderStep4() {
		// Get all input values, errors & items for employment status dropdown
		const { formData, errors, trusts, employmentStatuses } = this.state
		const addressErrors = errors ? {
			street: errors['address.street'],
			street_2: errors['address.street_2'],
			state: errors['address.state'],
			postal_code: errors['address.postal_code'],
			city: errors['address.city'],
			region: errors['address.region']
		} : {}

		// Get active process from props
		const { process } = this.props

		const noOptionsMessage = () => "Please enter the first three characters of your NHS trust to search"

		return (
			<>
				<InputFactory
					type="text"
					name="first_name"
					label="First Name*"
					placeholder="Name"
					value={formData?.first_name}
					error={errors?.first_name}
					onChange={this.handleInputChange}
					onKeyPress={this.handleKeyPress}
				/>
				<InputFactory
					type="text"
					name="last_name"
					label="Surname*"
					placeholder="Surname"
					value={formData?.last_name}
					error={errors?.last_name}
					onChange={this.handleInputChange}
					onKeyPress={this.handleKeyPress}
				/>
				<InputFactory
					type="address"
					name="address"
					label="Address*"
					tooltip={<FontAwesomeIcon icon={faInfoCircle} data-tip data-for="tooltip-address" className='tooltip-icon' />}
					placeholder="Address"
					value={formData?.address}
					error={addressErrors}
					onChange={this.handleInputChange}
					onKeyPress={this.handleKeyPress}
				/>
				<ReactTooltip id="tooltip-address" place="right" backgroundColor="#fff" textColor="#005EB8">
					<span>This form uses an automatic address look-up. It is usually correct, but please double-check your address when selecting from the suggestions</span>
				</ReactTooltip>
				<InputFactory
					type="text"
					name="job_title"
					label="Job Title*"
					placeholder="Title"
					value={formData?.job_title}
					error={errors?.job_title}
					onChange={this.handleInputChange}
					onKeyPress={this.handleKeyPress}
				/>

				{ process === 'nhs-carer' &&
					<>
						<InputFactory
							type="select"
							name="trust_id"
							label="NHS Trust*"
							value={formData?.trust_id}
							error={errors?.trust_id}
							onChange={this.handleInputChange}
							options={trusts}
							className="select-field"
							requireSearch={true}
							requireSearchMinLength="3"
							noOptionsMessage={noOptionsMessage}
							onKeyPress={this.handleKeyPress}
						/>
					</>
				}

				<InputFactory
					type="text"
					name="phone"
					label="Mobile Number*"
					value={formData?.phone}
					error={errors?.phone}
					placeholder="Mobile Number"
					onChange={this.handleInputChange}
					onKeyPress={this.handleKeyPress}
				/>
				<InputFactory
					type="select"
					name="employment_status_id"
					label="Employment Status*"
					error={errors?.employment_status_id}
					onChange={this.handleInputChange}
					options={employmentStatuses}
					onKeyPress={this.handleKeyPress}
				/>
				<InputFactory
					type="password"
					name="password"
					label="Password*"
					placeholder="Min 6 characters"
					value={formData?.password}
					error={errors?.password}
					onChange={this.handleInputChange}
					onKeyPress={this.handleKeyPress}
				/>
				<InputFactory
					type="password"
					name="password_confirmation"
					label="Confirm Password*"
					placeholder="Min 6 characters"
					value={formData?.password_confirmation}
					error={errors?.password_confirmation}
					onChange={this.handleInputChange}
					onKeyPress={this.handleKeyPress}
				/>
			</>
		)
	}

	renderStep5() {
		// Get all input values & errors
		const { formData, errors } = this.state
		const addressErrors = errors ? {
			street: errors['address.street'],
			street_2: errors['address.street_2'],
			state: errors['address.state'],
			postal_code: errors['address.postal_code'],
			city: errors['address.city'],
			region: errors['address.region']
		} : {}

		return (
			<>
				<InputFactory
					type="text"
					name="name"
					label="Organisation Name*"
					placeholder="Name"
					value={formData?.name}
					error={errors?.name}
					onChange={this.handleInputChange}
					onKeyPress={this.handleKeyPress}
				/>
				<InputFactory
					type="address"
					name="address"
					label="Address*"
					placeholder="Address"
					value={formData?.address}
					error={addressErrors}
					onChange={this.handleInputChange}
					onKeyPress={this.handleKeyPress}
				/>

				<InputFactory
					type="checkbox"
					name="outside_england"
					label="Is your organisation registered outside of England?"
					value={formData?.outside_england}
					error={errors?.outside_england}
					onChange={this.handleInputChange}
				/>
				{formData?.outside_england === true
					? <p className="manual-check-info">Please note: Your organisation will be subject to a manual
						eligibility process as per our <a href="/terms" target="_blank">terms of service</a>.</p>
					: <InputFactory
						type="text"
						name="cqc_location_id"
						label="CQC Location ID*"
						placeholder="ID"
						value={formData?.cqc_location_id}
						error={errors?.cqc_location_id}
						onChange={this.handleInputChange}
						onKeyPress={this.handleKeyPress}
					/>}

				<InputFactory
					type="text"
					name="contact_name"
					label="Contact Name*"
					placeholder="Name"
					value={formData?.contact_name}
					error={errors?.contact_name}
					onChange={this.handleInputChange}
					onKeyPress={this.handleKeyPress}
				/>
				<InputFactory
					type="text"
					name="contact_tel"
					label="Contact Telephone Number*"
					value={formData?.contact_tel}
					error={errors?.contact_tel}
					onChange={this.handleInputChange}
					onKeyPress={this.handleKeyPress}
				/>
				<InputFactory
					type="email"
					name="contact_email"
					label="Contact Email*"
					placeholder="Email"
					value={formData?.contact_email}
					error={errors?.contact_email}
					onChange={this.handleInputChange}
					onKeyPress={this.handleKeyPress}
				/>
			</>
		)
	}

	async handlePreviousStep(event) {
		event.preventDefault()

		// Get current step from state
		const { step } = this.state

		// Get whether NHS from props
		const { process } = this.props

		// Go back to previous step if we're not at the first step
		if(process === 'nhs-carer' && step > 1) {
			this.setState({step: step - 1})
		}
		else if(process !== 'nhs-carer' && step > 0) {
			this.setState({step: step - 1})
		}
	}

	async handleNextStep() {
		// Get whether NHS from props
		const { process } = this.props
		// Get current step from state
		const { step, numSteps, registerButtonLottie } = this.state

		this.setState({
			isLoading: true
		})

		// Check if form validates with current data
		const response = await this.validate()

		if (response?.success) {
			if (typeof window !== 'undefined') {
				window.dataLayer = window.dataLayer || [];
				window.dataLayer.push({
					'event': 'register_step',
					'register_process': process,
					'register_step': step
				});
			}

			if (registerButtonLottie) {
				registerButtonLottie.loop = false
				registerButtonLottie.playSegments([[25, 50], [51, 52], [125, 157]], true)

				registerButtonLottie.loop = false
				registerButtonLottie.playSegments([[112, 153], [154, 155]], true)

				this.setState({ registerButtonLottieState : 'toRegistrationSuccess'});

				if (typeof window !== 'undefined') {
					window.dataLayer = window.dataLayer || [];
					window.dataLayer.push({
						'event': 'register_complete',
						'register_process': process
					});
				}

				setTimeout(() => {
					if (process === 'organisation' && response?.data?.created_at) {
						return navigate('/register/organisation/success')
					}
	
					if (response?.data?.registration_complete) {
						return navigate(`/register/carer/success/${response?.data?.registration_token}`)
					}
				}, 1500);
			}
		}
		else {
			if (registerButtonLottie) {
				registerButtonLottie.loop = false
				registerButtonLottie.playSegments([[15, 1], [1, 0]], true)
			}

			this.setState({registerButtonLottieState: 'error'})
		}

		// Set next step
		let nextStep = step + 1

		// Check whether we need to skip a step
		if (step === 2) {
			nextStep = 4
		}

		// Proceed to next step if validation is successful & we're not at the last step
		if (response?.success && step < numSteps) {
			this.setState({
				step: nextStep,
				isLoading: false
			})
		}
	}

	renderBackButton() {
		// Get current step & number of steps from state
		const { step, numSteps, firstStep } = this.state

		if (step < numSteps && step > firstStep) {
			return step < numSteps && <p>
				<button onClick={this.handlePreviousStep.bind(this)} className="link--back link--back-circled back">
					<span><FontAwesomeIcon icon={faArrowLeft}/> Back</span></button>
			</p>
		}
	}

	renderNextButton() {
		// Get current step & number of steps from state
		const { step, numSteps, registerButtonLottie, registerButtonLottieState } = this.state

		let buttonRegisterLottiePlayer = createRef()
		let buttonRegisterBgLottiePlayer = createRef()

		const buttonLottieHover = () => {
			if (registerButtonLottie &&
				registerButtonLottieState !== 'submitting' &&
				registerButtonLottieState !== 'toRegistrationSuccess') {
				registerButtonLottie.playSegments([[registerButtonLottie.currentFrame, 18], [19, 20]], true)
			}
		}
	
		const buttonLottieBlur = () => {
			if (registerButtonLottieState !== 'submitting' &&
				registerButtonLottieState !== 'toRegistrationSuccess') {
				registerButtonLottie.playSegments([[20, 2], [1, 0]], false)
			}
		}
	
		const buttonLottieClick = () => {
			if (!registerButtonLottieState || registerButtonLottieState === 'error') {
				this.setState({ registerButtonLottieState : 'submitting'})

				registerButtonLottie.loop = true
				registerButtonLottie.playSegments([[45, 69], [70, 113]], true)

				this.handleNextStep.bind(this)
			}
			else if (registerButtonLottieState === 'toRegistrationSuccess') {
				this.handleNextStep.bind(this)
			}
		}

		// Display next button if not at the last step
		if (step < numSteps) {
			return (
				<Button type="button" fixedButton isLoading={this.state.isLoading} block onClick={this.handleNextStep.bind(this)}>Next</Button>
			)
		}
		// Display complete button if at the last step
		else {
			return (
				//Display Lottie Player here
				<div className="register-lottie-container">
					<div className="register core">
						<button className='btn btn--lottie-wrap'
							onMouseEnter={buttonLottieHover}
							onMouseLeave={buttonLottieBlur} 
							onClick={(event) => buttonLottieClick(event)}>
								<Player
									src={buttonRegisterJson}
									ref={buttonRegisterLottiePlayer}
									lottieRef={(instance) => {
										this.setState({registerButtonLottie: instance})
									}}
									autoplay={false}
									loop={false}
									controls={false} 
								/>
						</button>
					</div>
					<div className="register bg">
						<Player
							src={buttonRegisterBgJson}
							ref={buttonRegisterBgLottiePlayer}
							lottieRef={(instance) => {
								this.setState({registerBgButtonLottie: instance})
							}}
							autoplay={false}
							loop={false}
							controls={false}
						/>
					</div>
				</div>
			)
		}
	}

	handleInputChange = (value, name, scope) => {
		// Get current form data from state
		const { formData, step } = this.state

		if (value) {
			if (scope) {
				formData[scope][name] = value
			}
			else {
				formData[name] = value
			}
		} else {
			// Remove field value
			if (scope) {
				delete formData[scope][name]
			}
			else {
				delete formData[name]
			}
		}

		// Set ID also as organisation_id, so it works in step 2
		if (name === 'id') {
			if (step === 0) {
				formData['organisation_id'] = {...value}
			}
			else {
				formData['trust_id'] = value
			}
		}

		// Clear CQC if whether org is outside England has changed
		// Prevents field from being validated when hidden
		if (name === 'outside_england') {
			delete formData['cqc_location_id']
		}

		// Update state of file input values
		this.setState({ formData })
	}

	onFormSubmit = (e) => {
		e.preventDefault()
		this.handleNextStep()
	}

	handleKeyPress = (e) => {
		// Check if the user has hit enter
		if (e.which === 10 || e.which === 13) {
			e.preventDefault()

			this.handleNextStep()
		}
	}

	async validate() {
		// Get current step and input values from state
		const { step, formData } = this.state

		// Get whether NHS from props
		const { process } = this.props

		// Post current form data
		const response = await authService.register(process, step, formData, true)

		// Check if request was successful or if any validation errors
		if (!response?.success || response?.errors) {
			// Add validation errors to state
			this.setState({
				errors: response?.errors,
				isLoading: false,
				registerButtonLottieState: 'error', // button can't be clicked
				registerBgButtonLottieState: 'error' // button can't be clicked
			})

			// Return false to fail validation check
			return response
		}

		// Return true for successful validation check
		return response
	}

	render() {
		// Get current step from state
		const { step } = this.state

		// Get active process from props
		const { process } = this.props

		return (
			<DuoSection>
				<div>
					<div className="content-wrap">
						<h1 className="color--endeavour">{ process !== 'organisation' ? 'Register' : 'Register your organisation' }</h1>
					</div>
				</div>
				<div>
					<div className="content-wrap">
						<form encType="multipart/form-data" autoComplete="off" onSubmit={this.onFormSubmit}>
							{ /* Render back button */ }
							{ this.renderBackButton() }
							{ /* Render step dependent on state */ }
							{ step === 0 && this.renderStep0() }
							{ step === 1 && this.renderStep1() }
							{ step === 2 && this.renderStep2() }
							{ step === 3 && this.renderStep3() }
							{ step === 4 && this.renderStep4() }
							{ step === 5 && this.renderStep5() }

							{ /* Render next & submit buttons */ }
							{ this.renderNextButton() }

						</form>
					</div>
				</div>
			</DuoSection>
		)
	}
}

export default RegisterForm
